import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';

import { Store } from '@ngrx/store';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { NotificationTheme } from '@fcom/ui-components';
import { Category } from '@fcom/dapi/api/models';
import { TravelerService } from '@fcom/dapi/interfaces';

import { getSeatType } from '../../../ancillaries/utils/ancillary.utils';
import { CommonBookingFeatureState, getBundleAncillaryVariantByCategorySelector } from '../../../../store';
import { isAncillaryBundle, isBundle, isSeatBundle } from '../../utils/category.utils';

const translationRoot = 'ancillaries.overlappingServicesNotification.';

const categoryTitleKey = (category: Category | undefined): string => `ancillaries.${category}.genericTitle`;

const upsellNotifications: OverlapNotificationType[] = ['convertServiceToSeatBundle'];

type OverlapNotificationType =
  | 'replaceServiceWithBundle'
  | 'replaceMultipleServicesWithBundle'
  | 'convertSeatBundleToSeatBundle'
  | 'removeServiceFromBundle'
  | 'removeServiceFromSeatBundle'
  | 'convertServiceToSeatBundle'
  | 'convertSeatBundleToSeat';

@Component({
  selector: 'fin-service-overlap-notification',
  templateUrl: 'service-overlap-notification.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ServiceOverlapNotificationComponent {
  private store$ = inject<Store<CommonBookingFeatureState>>(Store);

  private bundleVariant = computed(() =>
    this.store$.selectSignal(getBundleAncillaryVariantByCategorySelector(this.category()))()
  );

  readonly NotificationTheme = NotificationTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  category = input.required<Category>();
  overlappingServices = input.required<TravelerService[]>();

  continueSelection = output<string | undefined>();
  cancelSelection = output<void>();

  readonly currentCategoryKey = computed(() => categoryTitleKey(this.category()));

  readonly notificationData = computed(() => {
    const category = this.category();
    const overlappingServices = this.overlappingServices() ?? [];
    const overlapSeat = this.getOverlapSeat(overlappingServices);
    const overlapCount = overlappingServices.length;
    const currentServiceIsSeatBundle = isSeatBundle(category);
    const currentServiceIsAncillaryBundle = isAncillaryBundle(category);
    const bundleVariant = this.bundleVariant();
    const notificationType = this.getNotificationType(overlappingServices, currentServiceIsSeatBundle, bundleVariant);
    const isUpsell = upsellNotifications.includes(notificationType);

    return {
      messageKey: translationRoot + notificationType,
      ctaCategoryKey: categoryTitleKey(currentServiceIsSeatBundle ? Category.SEAT : category),
      ctaTextKey:
        translationRoot +
        (currentServiceIsAncillaryBundle || (currentServiceIsSeatBundle && overlapSeat)
          ? 'switchToCombo'
          : 'continueToSelect'),
      overlappingServices,
      singleOverlapService: {
        name: overlappingServices[0]?.displayName,
        categoryKey: categoryTitleKey(overlappingServices[0]?.parentCategory),
      },
      overlapSeat,
      overlapCount,
      theme: isUpsell ? NotificationTheme.SUCCESS : NotificationTheme.INFO,
      icon: isUpsell ? SvgLibraryIcon.DISCOUNTED_PRICE_OUTLINE : SvgLibraryIcon.INFO_OUTLINE,
    };
  });

  continueToSelection(preExistingSeatNumber: string | undefined): void {
    this.continueSelection.emit(preExistingSeatNumber);
  }

  private getOverlapSeat(overlappingServices: TravelerService[]): { seatType: string; seatNumber: string } | undefined {
    const seatOverlap = overlappingServices.find((service) => service.seatNumber && service.seatNumber !== '');

    if (!seatOverlap || !seatOverlap?.seatNumber || seatOverlap?.seatNumber === '') {
      return undefined;
    }

    const splitVariant = seatOverlap.variant.split('_').filter((part) => part !== 'BDL');
    const seatTypeKey = getSeatType(splitVariant).cmsKey;

    return { seatType: seatTypeKey, seatNumber: seatOverlap?.seatNumber };
  }

  private getNotificationType(
    overlappingServices: TravelerService[],
    currentServiceIsSeatBundle: boolean,
    bundleVariant: string | undefined
  ): OverlapNotificationType | undefined {
    const serviceCount = overlappingServices.length;

    // no overlaps
    if (serviceCount < 1) {
      return undefined;
    }

    if (this.category() === Category.SEAT) {
      return 'convertSeatBundleToSeat';
    }

    // if single overlap, we check if overlap is part of bundle so we can provide an upsell copy vs. replace one
    if (serviceCount === 1) {
      const overlap = overlappingServices[0];

      // select single service, break bundle
      if (!isBundle(this.category()) && isBundle(overlap.parentCategory)) {
        if (isSeatBundle(overlap.parentCategory)) {
          return 'removeServiceFromSeatBundle';
        }

        return 'removeServiceFromBundle';
      }

      // switch seat bundle to another seat bundle
      if (isSeatBundle(overlap.parentCategory) && currentServiceIsSeatBundle) {
        return 'convertSeatBundleToSeatBundle';
      }

      const overlapIsPartOfBundle = overlap.variant === bundleVariant;

      // replace single service with bundle, upsell if part of bundle, replace if not
      return currentServiceIsSeatBundle && overlapIsPartOfBundle
        ? 'convertServiceToSeatBundle'
        : 'replaceServiceWithBundle';
    }

    // multiple overlaps, replace all
    return 'replaceMultipleServicesWithBundle';
  }
}
